import React, { useEffect } from 'react'
import { Redirect, useParams } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Link from '@material-ui/core/Link'
import Typography from '@material-ui/core/Typography'
import { PageHeader } from 'components/page-header'
import InfoAlert from 'components/info-alert'
import AccountRouterLink from 'components/account-router-link'
import StreamSendMessageButton from 'components/stream-messaging/send-message-button'
import { useVirtualCoachingRequest } from './api-hooks'
import { useStyles } from './styles'
import VirtualCoachingCoachPlusGate from './coach-plus-gate'
import LoadError from './load-error'
import RequestStatusChip from './request-status-chip'
import ReplyForm from './reply-form'
import RequestResponse from './request-response'
import StudentFeedback from './student-feedback'
import { trackVirtualCoachingEvent, VIRTUAL_COACHING_EVENTS } from './analytics'
import {
  formatDate,
  formatDateTime,
  getDownloadUrl,
  getRequestsPath,
  getStudentName,
  isHttpsUrl,
  isOverdue,
  ONFORM_APP_URL,
  REQUEST_STATUS,
} from './utils'

const SummaryItem = ({ label, children }) => {
  const classes = useStyles()
  return (
    <Box>
      <Typography className={classes.summaryLabel}>{label}</Typography>
      <Typography component="div" className={classes.summaryValue}>
        {children}
      </Typography>
    </Box>
  )
}

const ContextItem = ({ label, value }) => {
  const classes = useStyles()
  if (!value) return null
  return (
    <Box>
      <Typography className={classes.summaryLabel}>{label}</Typography>
      <Typography className={classes.summaryValue}>{value}</Typography>
    </Box>
  )
}

const VirtualCoachingRequestDetails = () => {
  const classes = useStyles()
  const { requestId } = useParams()
  const { data, loading, error, refetch } = useVirtualCoachingRequest(requestId)
  const request = data?.virtualCoachingRequest

  useEffect(() => {
    if (!request?.id) return
    trackVirtualCoachingEvent(VIRTUAL_COACHING_EVENTS.REQUEST_VIEWED, {
      requestId: request.id,
      status: request.status,
      submittedAt: request.submittedAt,
    })
    // Track one view per request, not per refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id])

  if (loading && !request) return <LinearProgress color="primary" />
  if (error && !request) {
    return (
      <>
        <PageHeader
          title="Virtual Coaching"
          crumbs={[{ label: 'Virtual Coaching', to: getRequestsPath() }]}
        />
        <Box className={classes.page}>
          <LoadError
            error={error}
            title="We couldn't load this request"
            onRetry={() => refetch()}
          />
        </Box>
      </>
    )
  }
  if (!request) return <Redirect to="/404" />

  const golfer = getStudentName(request.student)
  const overdue = isOverdue(request)
  const isReady = request.status === REQUEST_STATUS.READY_TO_ANALYZE
  const isComplete = request.status === REQUEST_STATUS.COMPLETE
  const hasContext = request.golfLevel || request.struggle || request.goal
  const trackedProps = { requestId: request.id }

  return (
    <>
      <PageHeader
        title={`Virtual coaching for ${golfer}`}
        crumbs={[{ label: 'Virtual Coaching', to: getRequestsPath() }]}
      />
      <Box className={classes.page}>
        <Box className={classes.card}>
          <Box className={classes.summaryHeader}>
            <RequestStatusChip
              status={request.status}
              overdue={overdue}
              size="medium"
            />
            {request.student?.externalId ? (
              <StreamSendMessageButton
                userIds={[request.student.externalId]}
                variant="outlined"
                color="primary"
              >
                Message {request.student.firstName || 'golfer'}
              </StreamSendMessageButton>
            ) : null}
          </Box>
          <Box className={classes.summaryGrid}>
            <SummaryItem label="Golfer">{golfer}</SummaryItem>
            <SummaryItem label="Offering">
              {request.offering?.title || '—'}
            </SummaryItem>
            <SummaryItem label="Purchased">
              {formatDateTime(request.purchasedAt)}
            </SummaryItem>
            <SummaryItem label="Outstanding swing credits">
              {request.outstandingCredits ?? 0}
            </SummaryItem>
            {request.submittedAt ? (
              <SummaryItem label="Video submitted">
                {formatDateTime(request.submittedAt)}
              </SummaryItem>
            ) : null}
            {isReady && request.dueAt ? (
              <SummaryItem label="Deliver by">
                {formatDate(request.dueAt)}
              </SummaryItem>
            ) : null}
            {request.transaction?.id ? (
              <SummaryItem label="Transaction">
                <Link
                  component={AccountRouterLink}
                  to={`/account/payments/transactions/${request.transaction.id}`}
                >
                  View transaction
                </Link>
              </SummaryItem>
            ) : null}
          </Box>
        </Box>

        {request.status === REQUEST_STATUS.PURCHASED ? (
          <InfoAlert>
            <Typography variant="body2">
              {golfer} has not uploaded a swing video yet. You will get an email
              and a notification when it is ready to analyze.
            </Typography>
          </InfoAlert>
        ) : null}

        {request.video?.url ? (
          <Box className={classes.card}>
            <Typography className={classes.cardTitle}>
              {golfer}&apos;s swing video
            </Typography>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              className={classes.video}
              src={request.video.url}
              poster={request.video.thumbnailUrl || undefined}
              controls
              playsInline
            />
            <Box className={classes.actionRow}>
              {isHttpsUrl(request.video.url) ? (
                <Button
                  variant="outlined"
                  color="primary"
                  component="a"
                  href={getDownloadUrl(request.video.url)}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackVirtualCoachingEvent(
                      VIRTUAL_COACHING_EVENTS.DOWNLOAD_CLICKED,
                      trackedProps,
                    )
                  }
                >
                  Download video
                </Button>
              ) : null}
              {!isComplete ? (
                <Button
                  variant="contained"
                  color="primary"
                  component="a"
                  href={ONFORM_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackVirtualCoachingEvent(
                      VIRTUAL_COACHING_EVENTS.ONFORM_CLICKED,
                      trackedProps,
                    )
                  }
                >
                  Analyze in Onform
                </Button>
              ) : null}
            </Box>
            {hasContext ? (
              <Box className={classes.contextList}>
                <ContextItem label="Golf level" value={request.golfLevel} />
                <ContextItem
                  label="What they are struggling with"
                  value={request.struggle}
                />
                <ContextItem
                  label="What they hope to achieve"
                  value={request.goal}
                />
              </Box>
            ) : null}
          </Box>
        ) : null}

        {isReady ? <ReplyForm request={request} /> : null}
        {isComplete ? (
          <>
            <RequestResponse request={request} />
            <StudentFeedback request={request} />
          </>
        ) : null}
      </Box>
    </>
  )
}

const VirtualCoachingRequestDetailsPage = () => (
  <VirtualCoachingCoachPlusGate>
    <VirtualCoachingRequestDetails />
  </VirtualCoachingCoachPlusGate>
)

export default VirtualCoachingRequestDetailsPage
